import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  listPasskeys, registerPasskey, deletePasskey,
  passkeysSupported, passkeyErrorMessage,
} from '../passkeyApi';

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return null; }
}

export default function PasskeySetupModal({ onClose, onPasskeysChanged }) {
  const { t } = useTranslation();
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [removing, setRemoving] = useState('');
  const [error, setError]       = useState('');
  const supported = passkeysSupported();

  const refresh = useCallback(async () => {
    try {
      const list = await listPasskeys();
      setPasskeys(list);
      onPasskeysChanged?.(list.length > 0);
    } catch (err) {
      setError(err.response?.data?.error || t('passkey.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [onPasskeysChanged, t]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleAdd = async () => {
    setAdding(true); setError('');
    try {
      await registerPasskey();
      // Sign-in offers the passkey button only to browsers that have one.
      localStorage.setItem('lxops_has_passkey', 'true');
      await refresh();
    } catch (err) {
      setError(passkeyErrorMessage(err, t));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (credentialId) => {
    setRemoving(credentialId); setError('');
    try {
      const { remaining } = await deletePasskey(credentialId);
      if (!remaining) localStorage.removeItem('lxops_has_passkey');
      await refresh();
    } catch (err) {
      setError(err.response?.data?.error || t('passkey.errRemove'));
    } finally {
      setRemoving('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-sm rounded-2xl p-6 animate-fade-in"
        style={{ backgroundColor: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 hover:text-gray-400 transition-colors text-lg">
          ✕
        </button>

        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-indigo-950/60 border border-indigo-800/50 rounded-full
            flex items-center justify-center mx-auto mb-3">
            <span className="text-2xl">🪪</span>
          </div>
          <h3 className="text-lg font-bold text-white mb-1">{t('passkey.title')}</h3>
          <p className="text-gray-400 text-sm">{t('passkey.subtitle')}</p>
        </div>

        {!supported ? (
          <p className="text-sm text-amber-400 text-center">{t('passkey.unsupported')}</p>
        ) : (
          <>
            {error && <p className="text-red-400 text-sm text-center mb-4">{error}</p>}

            {loading ? (
              <p className="text-gray-500 text-sm text-center py-4">{t('common.loading')}</p>
            ) : (
              <div className="space-y-2 mb-4">
                {passkeys.length === 0 && (
                  <p className="text-gray-500 text-sm text-center py-2">{t('passkey.none')}</p>
                )}
                {passkeys.map((p) => (
                  <div key={p.credential_id}
                    className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl"
                    style={{ backgroundColor: 'var(--lx-muted)', border: '1px solid var(--lx-border)' }}>
                    <div className="min-w-0">
                      <p className="text-sm text-gray-200 truncate">{p.label}</p>
                      <p className="text-xs text-gray-500">
                        {p.last_used_at
                          ? t('passkey.lastUsed', { date: fmtDate(p.last_used_at) })
                          : t('passkey.addedOn', { date: fmtDate(p.created_at) })}
                      </p>
                    </div>
                    <button
                      onClick={() => handleRemove(p.credential_id)}
                      disabled={!!removing}
                      className="text-xs font-semibold px-2.5 py-1.5 rounded-lg flex-shrink-0 transition-colors disabled:opacity-50"
                      style={{ backgroundColor: 'rgba(127,29,29,0.3)', border: '1px solid rgba(185,28,28,0.5)', color: '#fca5a5' }}
                    >
                      {removing === p.credential_id ? '…' : t('common.remove')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <button
              onClick={handleAdd}
              disabled={adding || loading}
              className="w-full py-2.5 rounded-xl text-sm font-semibold btn-primary disabled:opacity-60"
            >
              {adding ? t('passkey.adding') : t('passkey.add')}
            </button>
            <p className="text-xs text-gray-600 text-center mt-3">{t('passkey.hint')}</p>
          </>
        )}
      </div>
    </div>
  );
}
